import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Users as UsersIcon, Plus, Trash2, KeyRound, Mail, Shield,
  ShieldCheck, ToggleLeft, ToggleRight, Loader2, X, Copy, Link as LinkIcon,
} from "lucide-react";
import { adminGet, adminPost, adminPut, adminDelete } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin" };

const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setUsers(await adminGet("/users"));
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  return { users, loading, reload: load };
};

const UsersManager = () => {
  const { user: currentUser } = useAuth();
  const { users, loading, reload } = useUsers();
  const [showAdd, setShowAdd] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  return (
    <div data-testid="admin-users-page" className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">
            Super Admin
          </div>
          <h1 className="mt-2 font-display font-black text-4xl text-[#0A192F] tracking-tighter">
            Users
          </h1>
          <p className="mt-3 text-[#0A192F]/70 max-w-2xl text-sm">
            Manage admin accounts. <strong>Super admins</strong> can edit content AND manage other users.{" "}
            <strong>Admins</strong> can edit content but cannot see this page.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="user-add-btn"
          className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-3 font-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors"
        >
          <Plus size={14} /> Add User
        </button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#0A192F]/40">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading…
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-[#F0F4F8] text-[10px] font-display uppercase tracking-wider text-[#0A192F]/60">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isMe={u.id === currentUser?.id}
                  onChanged={reload}
                  onReset={() => setResetTarget(u)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onCreated={(result) => {
            reload();
            if (result.mode === "invite") setInviteResult(result);
          }}
        />
      )}

      {inviteResult && (
        <InviteLinkModal result={inviteResult} onClose={() => setInviteResult(null)} />
      )}

      {resetTarget && (
        <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />
      )}
    </div>
  );
};

const UserRow = ({ user, isMe, onChanged, onReset }) => {
  const RoleIcon = user.role === "super_admin" ? ShieldCheck : Shield;

  const updateRole = async (role) => {
    try {
      await adminPut(`/users/${user.id}`, { role });
      toast.success(`Role updated to ${ROLE_LABEL[role]}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update role");
    }
  };

  const toggleActive = async () => {
    try {
      await adminPut(`/users/${user.id}`, { active: !user.active });
      toast.success(user.active ? "User deactivated" : "User reactivated");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    try {
      await adminDelete(`/users/${user.id}`);
      toast.success("User deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <tr className="border-t border-gray-100" data-testid={`user-row-${user.email}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-[#0A192F]">{user.name || "-"}</div>
        {isMe && <div className="text-[10px] text-[#D4AF37] font-display uppercase tracking-wider">You</div>}
      </td>
      <td className="px-4 py-3 text-[#0A192F]/80">{user.email}</td>
      <td className="px-4 py-3">
        <select
          value={user.role}
          onChange={(e) => updateRole(e.target.value)}
          disabled={isMe}
          data-testid={`user-role-select-${user.email}`}
          className="border border-gray-300 px-2 py-1.5 text-sm focus:border-[#D4AF37] focus:outline-none disabled:opacity-50"
        >
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
        <RoleIcon size={12} className="inline ml-2 text-[#D4AF37]" />
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 text-xs font-display uppercase tracking-wider font-bold ${user.active ? "text-green-700" : "text-red-700"}`}>
          {user.active ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <IconBtn onClick={onReset} title="Reset password" testId={`user-reset-${user.email}`}>
            <KeyRound size={14} />
          </IconBtn>
          <IconBtn
            onClick={toggleActive}
            disabled={isMe}
            title={user.active ? "Deactivate" : "Reactivate"}
            testId={`user-toggle-${user.email}`}
          >
            {user.active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
          </IconBtn>
          <IconBtn
            onClick={remove}
            disabled={isMe}
            title="Delete"
            testId={`user-delete-${user.email}`}
            danger
          >
            <Trash2 size={14} />
          </IconBtn>
        </div>
      </td>
    </tr>
  );
};

const IconBtn = ({ children, onClick, disabled, title, testId, danger }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-2 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
      danger
        ? "border-red-200 text-red-600 hover:bg-red-50"
        : "border-gray-300 text-[#0A192F] hover:border-[#D4AF37] hover:text-[#D4AF37]"
    }`}
  >
    {children}
  </button>
);

const AddUserModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: "", email: "", role: "admin", mode: "direct", password: "" });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.mode !== "direct") delete payload.password;
      const result = await adminPost("/users", payload);
      toast.success(result.mode === "invite" ? "Invite sent" : "User created");
      onCreated(result);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add User" onClose={onClose} testId="user-add-modal">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" value={form.name} onChange={update("name")} required testId="user-add-name" />
        <Field label="Email" type="email" value={form.email} onChange={update("email")} required testId="user-add-email" />
        <div>
          <Label>Role</Label>
          <select
            value={form.role}
            onChange={update("role")}
            data-testid="user-add-role"
            className="mt-2 w-full border border-gray-300 px-3 py-2.5 focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        <div>
          <Label>How to set their password</Label>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            <ModeOption
              active={form.mode === "direct"}
              onClick={() => setForm((s) => ({ ...s, mode: "direct" }))}
              icon={KeyRound}
              title="Set it now"
              body="Type a temporary password yourself"
              testId="user-add-mode-direct"
            />
            <ModeOption
              active={form.mode === "invite"}
              onClick={() => setForm((s) => ({ ...s, mode: "invite" }))}
              icon={Mail}
              title="Send invite"
              body="Email a 72-hour activation link"
              testId="user-add-mode-invite"
            />
          </div>
        </div>
        {form.mode === "direct" && (
          <Field
            label="Temporary Password"
            type="text"
            value={form.password}
            onChange={update("password")}
            placeholder="At least 8 characters"
            required
            testId="user-add-password"
          />
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-300 font-display text-xs uppercase tracking-wider font-bold hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="user-add-submit"
            className="px-5 py-2.5 bg-[#0A192F] text-white font-display text-xs uppercase tracking-wider font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin inline" /> : form.mode === "invite" ? "Send Invite" : "Create User"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const ModeOption = ({ active, onClick, icon: Icon, title, body, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left p-4 border-2 transition-colors ${
      active ? "border-[#D4AF37] bg-[#FFF9E6]" : "border-gray-200 hover:border-[#D4AF37]"
    }`}
  >
    <Icon size={18} className="text-[#D4AF37]" />
    <div className="mt-2 font-display font-bold text-sm text-[#0A192F]">{title}</div>
    <div className="text-xs text-[#0A192F]/70 mt-1">{body}</div>
  </button>
);

const InviteLinkModal = ({ result, onClose }) => {
  const copy = () => {
    navigator.clipboard.writeText(result.invite_link);
    toast.success("Invite link copied");
  };
  return (
    <Modal title="Invite Created" onClose={onClose} testId="user-invite-modal">
      <div className="space-y-4">
        {result.email_sent ? (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 text-sm text-green-800">
            ✓ We emailed the invite to <strong>{result.user.email}</strong>. The link is valid for 72 hours.
          </div>
        ) : (
          <div className="bg-[#FFF9E6] border-l-4 border-[#D4AF37] p-4 text-sm text-[#0A192F]">
            Email delivery isn't configured yet (no Resend API key). <strong>Copy this link</strong> and share it with{" "}
            <strong>{result.user.email}</strong> manually. The link is valid for 72 hours.
          </div>
        )}
        <div>
          <Label>Invite Link</Label>
          <div className="mt-2 flex gap-2">
            <input
              readOnly
              value={result.invite_link}
              data-testid="user-invite-link"
              className="flex-1 border border-gray-300 px-3 py-2.5 text-xs font-mono bg-gray-50"
              onClick={(e) => e.target.select()}
            />
            <button
              onClick={copy}
              data-testid="user-invite-copy"
              className="px-4 bg-[#0A192F] text-white font-display text-xs uppercase tracking-wider font-bold hover:bg-[#D4AF37] hover:text-[#0A192F]"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ResetPasswordModal = ({ user, onClose }) => {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await adminPost(`/users/${user.id}/reset-password`, { new_password: pwd });
      toast.success("Password reset. Share the new password with the user.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Reset password - ${user.email}`} onClose={onClose} testId="user-reset-modal">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-[#0A192F]/70">
          Type a new temporary password. Share it with the user out-of-band (email, secure chat) and have them change it on next login.
        </p>
        <Field
          label="New Password"
          type="text"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="At least 8 characters"
          required
          testId="user-reset-pwd"
        />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-300 font-display text-xs uppercase tracking-wider font-bold">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="user-reset-submit"
            className="px-5 py-2.5 bg-[#0A192F] text-white font-display text-xs uppercase tracking-wider font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin inline" /> : "Reset Password"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const Modal = ({ title, children, onClose, testId }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div
      data-testid={testId}
      onClick={(e) => e.stopPropagation()}
      className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto"
    >
      <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h2 className="font-display font-bold text-lg text-[#0A192F]">{title}</h2>
        <button onClick={onClose} className="text-[#0A192F]/60 hover:text-[#D4AF37]" aria-label="Close">
          <X size={18} />
        </button>
      </header>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const Label = ({ children }) => (
  <label className="font-display text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/70 font-semibold">
    {children}
  </label>
);

const Field = ({ label, testId, ...props }) => (
  <div>
    <Label>{label}</Label>
    <input
      {...props}
      data-testid={testId}
      className="mt-2 w-full border border-gray-300 px-3 py-2.5 focus:border-[#D4AF37] focus:outline-none"
    />
  </div>
);

export default UsersManager;
